import React, { useState } from 'react';
import Modal from './Modal';

interface DeleteAccountModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  loading?: boolean;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  open,
  onClose,
  onConfirm,
  loading = false
}) => {
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (confirmation.toLowerCase() !== 'delete') {
      setError('Please type "delete" to confirm');
      return;
    }

    try {
      await onConfirm();
      setConfirmation('');
    } catch {
      setError('Failed to delete account');
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className="p-6">
        <h2 className="text-xl font-semibold text-red-700 mb-4">Delete Account</h2>
        
        <div className="mb-6 space-y-4">
          <p className="text-stone-800">
            Are you sure you want to delete your account? This action cannot be undone.
          </p>
          
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <h3 className="text-sm font-semibold text-red-800 mb-2">What happens when you delete your account:</h3>
            <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
              <li>Your account will be permanently deleted</li>
              <li>All your estate planning data will be erased</li>
              <li>Your executor and beneficiary assignments will be removed</li>
              <li>Your documents will be permanently deleted</li>
              <li>You'll lose access to all your saved information</li>
            </ul>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Type "delete" to confirm
            </label>
            <input
              type="text"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="delete"
              disabled={loading}
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-stone-700 hover:bg-stone-50 rounded-md"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 text-white rounded-md ${
                loading ? 'bg-red-500 cursor-not-allowed' : 'bg-red-700 hover:bg-red-800'
              }`}
              disabled={loading}
            >
              {loading ? 'Deleting...' : 'Delete My Profile'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default DeleteAccountModal;
